import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Layout } from "@/components/site/Layout";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { submitContactRequest } from "@/lib/contact.functions";
import { Toaster } from "@/components/ui/sonner";


export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact & Booking — The Crew Paint Contractors" },
      { name: "description", content: "Call 682-454-5997 or email thecrew1962@gmail.com. Serving Arlington, TX." },
      { property: "og:title", content: "Contact The Crew Paint Contractors" },
      { property: "og:description", content: "Get in touch with Bart Baldwin. Fastest response is by phone." },
      { property: "og:url", content: "https://thecrewpainting.com/contact" },
    ],
    links: [{ rel: "canonical", href: "https://thecrewpainting.com/contact" }],
  }),
  component: Contact,
});

const schema = z.object({
  name: z.string().trim().min(1, "Please enter your name").max(100),
  phone: z.string().trim().min(7, "Please enter a valid phone").max(30),
  email: z.string().trim().email("Please enter a valid email").max(255),
  service: z.string().min(1, "Please select a service"),
  message: z.string().trim().max(1000).optional().or(z.literal("")),
});

function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const submitRequest = useServerFn(submitContactRequest);


  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = schema.safeParse(form);
    if (!result.success) {
      toast.error(result.error.issues[0]?.message ?? "Please check the form");
      return;
    }
    setSubmitting(true);
    try {
      await submitRequest({ data: result.data });
      toast.success("Thanks! Bart will call you ASAP.", {
        description: "We typically respond the same day.",
      });
      setForm({ name: "", phone: "", email: "", service: "", message: "" });
    } catch {
      toast.error("Something went wrong sending your request.", {
        description: "Please call 682-454-5997 and we'll get you taken care of.",
      });
    } finally {
      setSubmitting(false);
    }
  };


  const update = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Layout>
      <Toaster richColors position="top-right" />
      <section className="mx-auto max-w-7xl px-6 pt-20 pb-10">
        <p className="text-xs uppercase tracking-widest text-accent font-semibold">Contact</p>
        <h1 className="mt-3 font-display text-4xl md:text-5xl text-primary">Let's talk about your project</h1>
        <p className="mt-4 text-lg text-muted-foreground max-w-2xl">
          Calling is the absolute fastest way to reach us. Bart answers the phone directly and will get a quote on the calendar right away.
        </p>
      </section>

      <section className="mx-auto max-w-7xl px-6 grid lg:grid-cols-5 gap-10 pb-10">
        {/* Left */}
        <div className="lg:col-span-2 space-y-4">
          <a href="tel:6824545997" className="block rounded-2xl bg-primary text-primary-foreground p-8 hover:opacity-95 transition">
            <Phone className="h-6 w-6 text-accent" />
            <p className="mt-4 text-xs uppercase tracking-widest text-primary-foreground/70">Call (fastest)</p>
            <p className="mt-1 font-display text-3xl">682-454-5997</p>
            <p className="mt-2 text-sm text-primary-foreground/75">Bart picks up directly — no menus, no waiting.</p>
          </a>

          <a href="mailto:thecrew1962@gmail.com" className="block rounded-2xl border border-border bg-card p-7 hover:border-primary/30 transition">
            <Mail className="h-6 w-6 text-accent" />
            <p className="mt-4 text-xs uppercase tracking-widest text-muted-foreground">Email</p>
            <p className="mt-1 font-display text-xl text-primary">thecrew1962@gmail.com</p>
          </a>

          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-2xl border border-border bg-card p-6">
              <MapPin className="h-5 w-5 text-accent" />
              <p className="mt-3 text-xs uppercase tracking-widest text-muted-foreground">Based in</p>
              <p className="mt-1 font-display text-lg text-primary">Arlington, TX</p>
            </div>
            <div className="rounded-2xl border border-border bg-card p-6">
              <Clock className="h-5 w-5 text-accent" />
              <p className="mt-3 text-xs uppercase tracking-widest text-muted-foreground">Response</p>
              <p className="mt-1 font-display text-lg text-primary">Same day</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="lg:col-span-3 rounded-2xl border border-border bg-card p-8 md:p-10">
          <h2 className="font-display text-2xl text-primary">Request a quote</h2>
          <p className="mt-1 text-sm text-muted-foreground">Fill this out and Bart will call you ASAP.</p>

          <form onSubmit={onSubmit} className="mt-6 space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Name">
                <input required value={form.name} onChange={update("name")} maxLength={100} className={inputCls} placeholder="Jane Smith" />
              </Field>
              <Field label="Phone Number">
                <input required type="tel" value={form.phone} onChange={update("phone")} maxLength={30} className={inputCls} placeholder="817-555-0100" />
              </Field>
            </div>
            <Field label="Email">
              <input required type="email" value={form.email} onChange={update("email")} maxLength={255} className={inputCls} placeholder="you@example.com" />
            </Field>
            <Field label="Service Needed">
              <select required value={form.service} onChange={update("service")} className={inputCls}>
                <option value="">Select a service…</option>
                <option>Painting</option>
                <option>Siding</option>
                <option>Drywall</option>
                <option>Other</option>
              </select>
            </Field>
            <Field label="Message">
              <textarea value={form.message} onChange={update("message")} maxLength={1000} rows={4} className={inputCls} placeholder="Tell us a bit about the project…" />
            </Field>
            <button
              type="submit"
              disabled={submitting}
              className="w-full sm:w-auto inline-flex items-center justify-center rounded-md bg-accent px-6 py-3.5 text-base font-semibold text-accent-foreground hover:opacity-90 disabled:opacity-60"
            >
              {submitting ? "Sending…" : "Request a Quote"}
            </button>
            <p className="text-xs text-muted-foreground">Prefer to call? Dial 682-454-5997 for the fastest response.</p>
          </form>
        </div>
      </section>
    </Layout>
  );
}

const inputCls =
  "w-full rounded-md border border-input bg-background px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-foreground mb-1.5">{label}</span>
      {children}
    </label>
  );
}
